import React from 'react';
import { Input, Select, Button, Avatar, ConfigProvider } from 'antd';
import { SearchOutlined, ExportOutlined } from '@ant-design/icons';

// --- Types ---
type ActivityType = 'call' | 'contact' | 'whatsapp' | 'email' | 'meeting';

interface ActivityItem {
  id: string;
  userName: string;
  userId: string;
  userAvatar: string;
  timestamp: string;
  type: ActivityType;
  meta?: string; // e.g., "5 min" or "Tue 3:00 PM"
  targetName?: string;
  targetAvatar?: string;
  targetEmail?: string;
  subActionText?: string;
}

// --- Mock Data ---
const activities: ActivityItem[] = [
  {
    id: '1',
    userName: 'Riya Sharma',
    userId: 'AG-0000',
    userAvatar: 'https://api.dicebear.com/7.x/avataaars/svg?seed=Riya',
    timestamp: '10 Sept 2024 • 5:30 PM',
    type: 'call',
    meta: '5 min',
    targetName: 'Sneha M.',
    targetAvatar: 'https://api.dicebear.com/7.x/avataaars/svg?seed=Sneha',
  },
  {
    id: '2',
    userName: 'Sneha M.',
    userId: 'AG-0000',
    userAvatar: 'https://api.dicebear.com/7.x/avataaars/svg?seed=Sneha',
    timestamp: '10 Sept 2024 • 5:30 PM',
    type: 'contact',
    targetName: 'Sarah',
    targetAvatar: 'https://api.dicebear.com/7.x/avataaars/svg?seed=Sarah',
    subActionText: 'Created by Sneha M.',
  },
  {
    id: '3',
    userName: 'Riya Sharma',
    userId: 'AG-0000',
    userAvatar: 'https://api.dicebear.com/7.x/avataaars/svg?seed=Riya',
    timestamp: '10 Sept 2024 • 5:30 PM',
    type: 'whatsapp',
    subActionText: 'Message by Sneha M.',
    targetAvatar: 'https://api.dicebear.com/7.x/avataaars/svg?seed=Sneha',
  },
  {
    id: '4',
    userName: 'Riya Sharma',
    userId: 'AG-0000',
    userAvatar: 'https://api.dicebear.com/7.x/avataaars/svg?seed=Riya',
    timestamp: '10 Sept 2024 • 5:30 PM',
    type: 'email',
    targetEmail: '@example.com',
    targetAvatar: 'https://api.dicebear.com/7.x/avataaars/svg?seed=Sneha',
  },
  {
    id: '5',
    userName: 'Riya Sharma',
    userId: 'AG-0000',
    userAvatar: 'https://api.dicebear.com/7.x/avataaars/svg?seed=Riya',
    timestamp: '10 Sept 2024 • 5:30 PM',
    type: 'meeting',
    meta: 'Tue 3:00 PM',
    targetEmail: '@example.com',
    targetAvatar: 'https://api.dicebear.com/7.x/avataaars/svg?seed=Sneha',
  },
];

const ActivityTimeline: React.FC = () => {
  return (
    <ConfigProvider
      theme={{
        token: {
          borderRadius: 4,
          colorPrimary: '#1677ff',
        },
      }}
    >
      <div className="w-full bg-white border border-gray-200 rounded-sm shadow-sm overflow-hidden font-sans">
        
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 bg-white sticky top-0 z-10">
          <h2 className="text-[15px] font-semibold text-gray-700">Activity Timeline</h2>
          <div className="flex items-center gap-2">
            <Input 
              placeholder="Search" 
              suffix={<SearchOutlined className="text-gray-400" />} 
              className="w-48 h-8 text-sm"
            />
            <Select placeholder="Select Employee" className="w-40 h-8 text-sm" options={[]} />
            <Select placeholder="Select Type" className="w-32 h-8 text-sm" options={[]} />
            <Button icon={<ExportOutlined />} className="h-8 w-8 flex items-center justify-center border-gray-200" />
          </div>
        </div>

        {/* Timeline Content */}
        <div className="flex flex-col">
          {activities.map((item) => (
            <div key={item.id} className="group px-6 py-4 flex gap-4 border-b border-gray-50 last:border-b-0 hover:bg-gray-50/50 transition-colors">
              
              {/* Left Side: Avatar */}
              <div className="shrink-0 mt-1">
                <Avatar src={item.userAvatar} size={40} className="border border-gray-100" />
              </div>

              {/* Right Side: Content */}
              <div className="flex-1 min-w-0">
                <div className="flex justify-between items-start mb-1">
                  <div>
                    <h3 className="text-[14px] font-bold text-gray-800 leading-none">{item.userName}</h3>
                    <span className="text-[11px] text-gray-400 font-medium tracking-tight uppercase">{item.userId}</span>
                  </div>
                  <time className="text-[12px] text-gray-400">{item.timestamp}</time>
                </div>

                {/* Conditional Rendering based on Activity Type */}
                <div className="mt-3 text-[13px]">
                  {item.type === 'call' && (
                    <div className="space-y-1.5">
                      <p className="text-gray-700 font-medium">Outgoing call • {item.meta}</p>
                      <div className="flex items-center gap-2 text-gray-500">
                        <span>Call with</span>
                        <Avatar src={item.targetAvatar} size={22} />
                        <span className="text-gray-900 font-semibold">{item.targetName}</span>
                      </div>
                    </div>
                  )}

                  {item.type === 'contact' && (
                    <div className="space-y-1.5">
                      <div className="flex items-center gap-2 font-medium text-gray-700">
                        <span>Contact Created by Lead:</span>
                        <Avatar src={item.targetAvatar} size={22} />
                        <span className="text-gray-900 font-semibold">{item.targetName}</span>
                      </div>
                      <div className="flex items-center gap-1.5 text-gray-400 text-[12px]">
                        <span>Created by</span>
                        <Avatar src={item.userAvatar} size={18} />
                        <span>{item.userName}</span>
                      </div>
                    </div>
                  )}

                  {item.type === 'whatsapp' && (
                    <div className="space-y-1.5">
                      <p className="text-gray-700 font-medium">WhatsApp Message</p>
                      <div className="flex items-center gap-2 text-gray-500">
                        <span>Message by</span>
                        <Avatar src={item.targetAvatar} size={22} />
                        <span className="text-gray-900 font-semibold">{item.targetName || 'Sneha M.'}</span>
                      </div>
                    </div>
                  )}

                  {item.type === 'email' && (
                    <div className="space-y-1.5">
                      <p className="text-gray-700 font-medium">Email Sent</p>
                      <div className="flex items-center gap-2">
                        <span className="text-gray-500">to</span>
                        <Avatar src={item.targetAvatar} size={22} />
                        <span className="text-blue-500 hover:underline cursor-pointer">{item.targetEmail}</span>
                      </div>
                    </div>
                  )}

                  {item.type === 'meeting' && (
                    <div className="space-y-1.5">
                      <p className="text-gray-700 font-medium">Meeting Scheduled • {item.meta}</p>
                      <div className="flex items-center gap-2">
                        <span className="text-gray-400 text-[12px]">Scheduled by {item.userName} for Lead:</span>
                        <Avatar src={item.targetAvatar} size={22} />
                        <span className="text-blue-500 hover:underline cursor-pointer">{item.targetEmail}</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </ConfigProvider>
  );
};

export default ActivityTimeline;